import React, { useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPlane, 
    faLocationDot, 
    faGauge, 
    faUsers, 
    faRulerHorizontal,
    faCheckCircle,
    faPlaneCircleCheck,
    faClock,
    faWrench,
    faPlaneUp,
    faLock
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../components/auth-context/auth';
import api from '../../api/api';
import './fleet.scss';

export function Fleet() {
    const { t } = useTranslation();
    const { isAuthenticated } = useContext(AuthContext);
    const [aircraftModels, setAircraftModels] = useState([]);
    const [selectedAircraft, setSelectedAircraft] = useState(null);
    const [fleetDetails, setFleetDetails] = useState([]);
    const [showDetails, setShowDetails] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [error, setError] = useState(null);
    const [failedImages, setFailedImages] = useState(new Set());

    const getStateInfo = (state) => {
        switch(state) {
            case 0: return { icon: faCheckCircle, color: '#4CAF50', text: t('fleet-state-free') || 'Available' };
            case 1: return { icon: faClock, color: '#FF9800', text: t('fleet-state-reserved') || 'Reserved' };
            case 2: return { icon: faPlaneCircleCheck, color: '#2196F3', text: t('fleet-state-in-flight') || 'In Flight' };
            case 3: return { icon: faWrench, color: '#F44336', text: t('fleet-state-maintenance') || 'Maintenance' };
            default: return { icon: faCheckCircle, color: '#757575', text: 'Unknown' };
        }
    };

    useEffect(() => {
        fetchAircraftModels();
    }, []);

    const fetchAircraftModels = async () => {
        try {
            const response = await api.get('/fleet/models');
            if (response?.data) {
                setAircraftModels(response.data);
                setError(null);
            }
        } catch (error) {
            console.error('Failed to fetch aircraft models:', error);
            setError(t('failed-to-fetch-fleet') || 'Failed to fetch fleet');
        } finally {
            setLoading(false);
        }
    };

    const fetchFleetDetails = async (aircraftId) => {
        if (!isAuthenticated) return;
        
        setLoadingDetails(true);
        try {
            const response = await api.get(`/fleet/model/${aircraftId}`);
            if (response?.data) {
                setFleetDetails(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch fleet details:', error);
            setFleetDetails([]);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleAircraftClick = async (aircraft) => {
        setSelectedAircraft(aircraft);
        setShowDetails(true);
        if (isAuthenticated) {
            await fetchFleetDetails(aircraft.id);
        }
    };

    const closeDetails = () => {
        setShowDetails(false);
        setTimeout(() => setSelectedAircraft(null), 300);
    };

    if (loading) {
        return (
            <div className="fleet-container">
                <div className="fleet-loading">
                    <FontAwesomeIcon icon={faPlane} className="loading-icon" />
                    <p>{t('loading') || 'Loading fleet data...'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fleet-container">
            <div className="fleet-header">
                <h1>
                    <FontAwesomeIcon icon={faPlane} /> {t('fleet') || 'Our Fleet'}
                </h1>
                <p className="fleet-subtitle">
                    {t('fleet-subtitle') || 'Discover our modern aircraft fleet, maintained to the highest standards for your virtual flying experience'}
                </p>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="fleet-grid">
                {aircraftModels.map((aircraft) => (
                    <div 
                        key={aircraft.id} 
                        className="aircraft-card"
                        onClick={() => handleAircraftClick(aircraft)}
                    >
                        <div className="aircraft-image-container">
                            {aircraft.img && !failedImages.has(aircraft.id) ? (
                                <img 
                                    src={aircraft.img} 
                                    alt={aircraft.model}
                                    className="aircraft-image"
                                    onError={() => {
                                        setFailedImages(prev => new Set(prev).add(aircraft.id));
                                    }}
                                />
                            ) : (
                                <div className="aircraft-placeholder">
                                    <FontAwesomeIcon icon={faPlaneUp} />
                                    <span>{aircraft.icao}</span>
                                </div>
                            )}
                            
                            <div className="aircraft-overlay">
                                <div className="overlay-top">
                                    <div className="aircraft-specs-badge">
                                        <div className="spec-line">
                                            <FontAwesomeIcon icon={faUsers} />
                                            <span>{aircraft.max_passengers}</span>
                                        </div>
                                        <div className="spec-line">
                                            <FontAwesomeIcon icon={faRulerHorizontal} />
                                            <span>{aircraft.range} nm</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="overlay-bottom">
                                    <h3 className="aircraft-model">{aircraft.model}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {showDetails && selectedAircraft && (
                <div className={`fleet-modal ${showDetails ? 'show' : ''}`} onClick={closeDetails}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeDetails}>×</button>
                        
                        <div className="modal-header">
                            <h2>{selectedAircraft.model}</h2>
                        </div>

                        <div className="modal-body">
                            <div className="modal-specs">
                                <h3>{t('aircraft-specifications') || 'Aircraft Specifications'}</h3>
                                <div className="specs-grid">
                                    <div className="spec-item">
                                        <span className="spec-label">{t('icao-code') || 'ICAO Code'}:</span>
                                        <span className="spec-value">{selectedAircraft.icao}</span>
                                    </div>
                                    <div className="spec-item">
                                        <span className="spec-label">{t('manufacturer') || 'Manufacturer'}:</span>
                                        <span className="spec-value">{selectedAircraft.manufacturer}</span>
                                    </div>
                                    <div className="spec-item">
                                        <span className="spec-label">{t('max-passengers') || 'Max Passengers'}:</span>
                                        <span className="spec-value">{selectedAircraft.max_passengers}</span>
                                    </div>
                                    <div className="spec-item">
                                        <span className="spec-label">{t('range') || 'Range'}:</span>
                                        <span className="spec-value">{selectedAircraft.range} nm</span>
                                    </div>
                                    <div className="spec-item">
                                        <span className="spec-label">{t('fleet-size') || 'Fleet Size'}:</span>
                                        <span className="spec-value">{selectedAircraft.totalUnits} {t('aircraft') || 'aircraft'}</span>
                                    </div>
                                    <div className="spec-item">
                                        <span className="spec-label">{t('currently-available') || 'Currently Available'}:</span>
                                        <span className="spec-value">{selectedAircraft.availableUnits}</span>
                                    </div>
                                </div>
                            </div>

                            {isAuthenticated ? (
                                <div className="modal-fleet-list">
                                    <h3>{t('fleet-units') || 'Fleet Units'} ({fleetDetails.length})</h3>
                                    {loadingDetails ? (
                                        <div className="loading-details">
                                            <FontAwesomeIcon icon={faPlane} spin />
                                            <span>{t('loading') || 'Loading...'}</span>
                                        </div>
                                    ) : (
                                        <div className="fleet-units-scroll">
                                            <div className="fleet-units">
                                                {fleetDetails.map((plane) => {
                                                    const stateInfo = getStateInfo(plane.state);
                                                    return (
                                                        <div key={plane.id} className="fleet-unit">
                                                            <div className="unit-reg">{plane.reg}</div>
                                                            <div className="unit-name">{plane.name}</div>
                                                            <div className="unit-location">
                                                                <FontAwesomeIcon icon={faLocationDot} />
                                                                {' '}
                                                                {plane.location?.icao || 'N/A'}
                                                            </div>
                                                            <div className="unit-state" style={{ color: stateInfo.color }}>
                                                                <FontAwesomeIcon icon={stateInfo.icon} />
                                                                {' '}
                                                                {stateInfo.text}
                                                            </div>
                                                            <div className="unit-life">
                                                                <FontAwesomeIcon icon={faGauge} />
                                                                {' '}
                                                                {plane.life}%
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="login-prompt">
                                    <FontAwesomeIcon icon={faLock} />
                                    <p>{t('login-to-see-details') || 'Login to see fleet details'}</p>
                                    <a href="/login" className="btn-login">
                                        {t('login') || 'Login'}
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}